import React, { useState, useMemo, useContext } from "react";
import CrosstabModal from "../DashboardBuilder/CrosstabModal";
import "../styles/SidebarDashboard.css";
import FetchingFreq from "../../contexts/FetchingFreq";
import { useToast } from "../../contexts/ToastContext";

export default function Sidebar({
  dataGroup,
  selectedQuestionKey,
  setSelectedQuestionKey,
  handleAddKPI,
  handleAddFilter,
  handleAddGlobalFilter,
  globalFilters = [],
  selectedTab,
  selectedVariable,
  crosstabDisplayData,
  setCrosstabDisplayData,
  handleAddGlobalBanner,
  handleAddTabBanner,
  globalBanners = [],
  handleRemoveGlobalBanner,
}) {
  const { handleTitleClick } = useContext(FetchingFreq);
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [multiSelected, setMultiSelected] = useState([]);
  const [showCrosstabModal, setShowCrosstabModal] = useState(false);

  const sortedEntries = Object.entries(dataGroup || {}).sort(([, a], [, b]) => {
    const orderA = a?.order !== undefined ? Number(a.order) : (a?._order !== undefined ? Number(a._order) : Infinity);
    const orderB = b?.order !== undefined ? Number(b.order) : (b?._order !== undefined ? Number(b._order) : Infinity);
    const validA = isNaN(orderA) ? Infinity : orderA;
    const validB = isNaN(orderB) ? Infinity : orderB;
    return validA - validB;
  });

  const filteredEntries = useMemo(
    () =>
      sortedEntries.filter(([key, entry]) =>
        entry != null &&
        (entry.title || key).toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [searchTerm, sortedEntries]
  );

  const handleAddKPIClick = () => {
    if (multiSelected.length === 0) {
      toast.warning("Please select at least one variable.");
      return;
    }
    handleAddKPI(multiSelected);
    setMultiSelected([]);
    setSelectedQuestionKey("");
  };

  const handleLocalFilterClick = () => {
    if (!selectedQuestionKey) {
      toast.warning("Please select a variable first.");
      return;
    }
    if (globalFilters.some((f) => f.variable_name === selectedQuestionKey)) {
      toast.warning("This variable is already added as a Global Filter.");
      return;
    }
    if (selectedTab?.filters?.some((f) => f.variable_name === selectedQuestionKey)) {
      toast.warning("This variable is already added as a Local Filter in this tab.");
      return;
    }
    handleAddFilter();
  };

  const handleGlobalFilterClick = () => {
    if (!selectedQuestionKey) {
      toast.warning("Please select a variable first.");
      return;
    }
    if (globalFilters.some((f) => f.variable_name === selectedQuestionKey)) {
      toast.warning("This variable is already added as a Global Filter.");
      return;
    }
    handleAddGlobalFilter();
  };

  return (
    <div className="d-sidebar">
      <div className="d-sidebar-header">
        <span className="d-sidebar-title">Variables</span>
      </div>

      {/* Action buttons */}
      <div className="d-sidebar-buttons">
        <button className="sb-btn sb-btn--kpi" onClick={handleAddKPIClick}>
          <span className="sb-btn-icon">➕</span>
          <span className="sb-btn-label">
            Add KPI{multiSelected.length > 0 ? ` (${multiSelected.length})` : ""}
          </span>
        </button>
        <button className="sb-btn sb-btn--local" onClick={handleLocalFilterClick}>
          <span className="sb-btn-icon">🎯</span>
          <span className="sb-btn-label">Local Filter</span>
        </button>
        <button className="sb-btn sb-btn--global" onClick={handleGlobalFilterClick}>
          <span className="sb-btn-icon">🌐</span>
          <span className="sb-btn-label">Global Filter</span>
        </button>
        <button className="sb-btn sb-btn--tab-banner" onClick={handleAddTabBanner}>
          <span className="sb-btn-icon">📊</span>
          <span className="sb-btn-label">Tab Banner</span>
        </button>
        <button className="sb-btn sb-btn--global-banner" onClick={handleAddGlobalBanner}>
          <span className="sb-btn-icon">🌍</span>
          <span className="sb-btn-label">Global Banner</span>
        </button>
      </div>

      {/* Global banners list */}
      {globalBanners.length > 0 && (
        <div className="d-sidebar-banner-section">
          <span className="d-sidebar-banner-title">Global Banners</span>
          {globalBanners.map((b) => (
            <div key={b.key} className="d-sidebar-banner-chip">
              <span className="d-sidebar-banner-chip-label">{b.label || b.key}</span>
              <button
                className="d-sidebar-banner-chip-remove"
                onClick={() => handleRemoveGlobalBanner(b.key)}
                aria-label="Remove global banner"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="d-sidebar-search-wrap">
        <svg className="d-sidebar-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <input
          type="text"
          className="d-sidebar-search"
          placeholder="Search variables..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {searchTerm && (
          <button
            className="d-sidebar-search-clear"
            onClick={() => setSearchTerm("")}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {/* Selected variable info */}
      {selectedVariable && (
        <div className="selected-var-info">
          <strong>{selectedVariable.Qtitle || selectedVariable.title}</strong>
          <span className="selected-var-type">
            {selectedVariable.questionType || selectedVariable.type}
          </span>
        </div>
      )}

      {/* Variable list */}
      <div className="variable-section">
        {filteredEntries.length > 0 ? (
          filteredEntries.map(([key, entry]) => {
            const isLocalFilter = selectedTab?.filters?.some(
              (f) => f.variable_name === key
            );
            const isGlobalFilter = globalFilters.some(
              (f) => f.variable_name === key
            );
            const isSelected = multiSelected.includes(key);
            const isActive = selectedQuestionKey === key;
            const isDisabled = !!entry?.removeTab;

            return (
              <div
                key={key}
                className={[
                  "variable-item",
                  isSelected ? "multi-selected" : "",
                  isActive ? "selected" : "",
                  isDisabled ? "disabled-variable" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => {
                  if (isDisabled) {
                    toast.warning(
                      `Table run is not allowed for "${entry.title || key}" — turn off "Remove Tab" for this object first.`
                    );
                    return;
                  }
                  if (isSelected) {
                    setMultiSelected(multiSelected.filter((i) => i !== key));
                  } else {
                    setMultiSelected([...multiSelected, key]);
                  }
                  setSelectedQuestionKey(key);
                  handleTitleClick(key);
                }}
                title={entry.title || key}
              >
                <span className="variable-item-label">{entry.title || key}</span>
                {(isLocalFilter || isGlobalFilter) && (
                  <span className="variable-item-badges">
                    {isLocalFilter && (
                      <span className="var-badge var-badge--local">Local</span>
                    )}
                    {isGlobalFilter && (
                      <span className="var-badge var-badge--global">Global</span>
                    )}
                  </span>
                )}
              </div>
            );
          })
        ) : (
          <p className="no-data">
            {searchTerm ? `No variables match "${searchTerm}"` : "No variables found"}
          </p>
        )}
      </div>

      {showCrosstabModal && (
        <CrosstabModal
          selectedVars={sortedEntries.map(([k]) => k)}
          onClose={() => setShowCrosstabModal(false)}
          onGenerate={() => setShowCrosstabModal(false)}
          crosstabDisplayData={crosstabDisplayData}
          setCrosstabDisplayData={setCrosstabDisplayData}
        />
      )}
    </div>
  );
}